/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useEffect, useState } from "react";
import { ConfirmModal, TermAndServiceContent } from "@advapacs/common";
import axios from "axios";
import { EulaAPI } from "@src/config/api";
import { LoginAction } from "@src/reducers/slices/loginSlice";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "@src/config/store";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";
import { useLocation, useNavigate } from "react-router-dom";
import { UserLicenseActions } from "@src/pages/authenticated/userLicense/ducks/slices";
import { UnauthenticatedActions } from "@src/pages/unauthenticated/ducks/slices";
import { getEulaLatest } from "./ducks/operators";
import { redirectLoginPage } from "@src/utils/utils";

export const TermsOfServicePage: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isShowEula } = useSelector((state: IRootState) => state.LoginReducer);
  const { eulaDetail } = useSelector(
    (state: IRootState) => state.userLicenseReducer
  );
  const [showEula, setShowEula] = useState(false);

  useEffect(() => {
    if (eulaDetail?.eula?.id && isShowEula) {
      setShowEula(true);
      dispatch(LoginAction.setIsShowEulaAfterLogin(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eulaDetail, isShowEula]);

  useEffect(() => {
    if (location.pathname.includes("/public/terms-of-service")) {
      dispatch(getEulaLatest());
      setShowEula(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleLogoutWhenDecline = async () => {
    try {
      await axios.post(`/authenticatedPatient/logout`);
    } catch (error) {
    } finally {
      dispatch(UnauthenticatedActions.clearAuthenticationWhenDeclineEULA());
    }
  };

  const handleAcceptUserLicense = async () => {
    try {
      dispatch(UserLicenseActions.getEulaStart());
      await axios.patch(EulaAPI.ACCEPT_EULA);
      setShowEula(false);
      localStorage.removeItem("pp-token");
      navigate(`/Exams`);
      dispatch(UserLicenseActions.clearData());
    } catch (err) {
      handleLogoutWhenDecline();
    }
  };

  return (
    <>
      {showEula && (
        <ConfirmModal
          size="lg"
          show={showEula}
          onClose={() => {
            setShowEula(false);
          }}
          header={t("modal:eula.eula_pp_header")}
          body={
            <>
              <TermAndServiceContent
                content={eulaDetail?.eula?.content}
                title={
                  location.pathname.includes("/public/terms-of-service")
                    ? ""
                    : t("modal:eula.eula_pp_title")
                }
              />
            </>
          }
          cancelBtnVariant={"light"}
          cancelBtnName={
            location.pathname.includes("/public/terms-of-service")
              ? t("close")
              : t("button.decline")
          }
          confirmBtnVariant="primary"
          confirmBtnName={t("button.accept")}
          confirm={
            location.pathname.includes("/public/terms-of-service")
              ? null
              : handleAcceptUserLicense
          }
          cancel={() => {
            if (!location.pathname.includes("/public/terms-of-service")) {
              window.location.href = localStorage.getItem("pp-token")
                ? redirectLoginPage
                : "/";
              handleLogoutWhenDecline();
            } else {
              window.location.href = window.location.origin;
            }
            setShowEula(false);
          }}
        />
      )}
    </>
  );
};
